/**
 * Single-location Mapbox map for showroom pages (light-v10, matches Stockist tiles).
 */
(function () {
  const MAPBOX_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.8.0/mapbox-gl.js';
  const MAPBOX_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.8.0/mapbox-gl.css';
  const MAPBOX_RTL =
    'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.3.0/mapbox-gl-rtl-text.js';

  const DEFAULT_STYLE = 'mapbox://styles/mapbox/light-v10';
  const DEFAULT_ZOOM = 14;

  function loadStylesheet(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (window.mapboxgl) {
        resolve();
        return;
      }

      const existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function enableRTLText() {
    if (!window.mapboxgl || !mapboxgl.setRTLTextPlugin) return;

    try {
      mapboxgl.setRTLTextPlugin(MAPBOX_RTL, null, true);
    } catch (e) {
      // Plugin already set; ignore.
    }
  }

  function parseCoords(raw) {
    if (!raw) return null;

    try {
      const data = JSON.parse(raw);
      const point = Array.isArray(data) ? data[0] : data;
      if (!point) return null;

      const lat = parseFloat(point.lat ?? point.latitude);
      const lng = parseFloat(point.lng ?? point.longitude ?? point.lon);

      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    } catch (e) {
      /* not JSON */
    }

    const parts = raw.split(',');
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());

      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }

    return null;
  }

  function geocodeAddress(address, token) {
    const url =
      'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      encodeURIComponent(address) +
      '.json?access_token=' +
      encodeURIComponent(token) +
      '&limit=1';

    return fetch(url)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        const feature = data.features && data.features[0];
        if (!feature || !feature.center) return null;

        return { lng: feature.center[0], lat: feature.center[1] };
      })
      .catch(function () {
        return null;
      });
  }

  function initMap(el) {
    const token = (el.dataset.mapboxToken || '').trim();
    const address = (el.dataset.mapAddress || '').trim();
    const style = (el.dataset.mapboxStyle || DEFAULT_STYLE).trim() || DEFAULT_STYLE;

    if (!token) return;

    const coords = parseCoords(el.dataset.mapCoords);
    const hasCoords = coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng);

    const ready = hasCoords
      ? Promise.resolve(coords)
      : address
        ? geocodeAddress(address, token)
        : Promise.resolve(null);

    ready
      .then(function (resolved) {
        if (!resolved) {
          el.classList.add('showroom-detail__map--unavailable');
          return null;
        }

        return Promise.all([loadStylesheet(MAPBOX_CSS), loadScript(MAPBOX_JS)]).then(function () {
          enableRTLText();
          return resolved;
        });
      })
      .then(function (resolved) {
        if (!resolved || !window.mapboxgl) return;

        mapboxgl.accessToken = token;

        const map = new mapboxgl.Map({
          container: el,
          style: style,
          center: [resolved.lng, resolved.lat],
          zoom: DEFAULT_ZOOM,
          attributionControl: true,
        });

        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

        new mapboxgl.Marker()
          .setLngLat([resolved.lng, resolved.lat])
          .addTo(map);
      });
  }

  function init() {
    document.querySelectorAll('[data-showroom-map]').forEach(initMap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();